import LeadCaptureBox from "@/components/LeadCaptureBox";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import Spinner from "@/components/Spinner";
import { ArrowLeft } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";
import { sanitizeHtml } from "@/lib/sanitize";

const API = "/api/public";

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<any>(null);
  const [related, setRelated] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API}/blog/${slug}`)
      .then((r) => { if (!r.ok) { setNotFound(true); return null; } return r.json(); })
      .then((data) => { if (data) { setPost(data.post); setRelated(data.related || []); } })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <Spinner className="py-32" />;
  if (notFound || !post) return (
    <div className="max-w-4xl mx-auto px-6 py-32 text-center">
      <h1 className="text-2xl font-bold mb-2">Post not found</h1>
      <p className="text-slate mb-6">This article doesn't exist or has been removed.</p>
      <Link to="/blog" className="text-coral font-semibold hover:underline">← Back to blog</Link>
    </div>
  );

  return (
    <>
      <SeoHead
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt || ""}
        ogImage={post.og_image}
        ogType="article"
        url={`${SITE_URL}/blog/${post.slug}`}
        article={{
          publishedTime: post.published_at,
          author: post.author_name,
          category: post.category,
          tags: post.tags,
        }}
        breadcrumbs={[
          { name: "Home", url: SITE_URL },
          { name: "Blog", url: `${SITE_URL}/blog` },
          { name: post.title, url: `${SITE_URL}/blog/${post.slug}` },
        ]}
      />

      <article className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-slate hover:text-ink mb-8">
          <ArrowLeft size={14} /> Back to blog
        </Link>

        <div className="flex items-center gap-3 mb-4">
          {post.category && (
            <span className="px-2.5 py-0.5 bg-coral-light text-coral text-xs font-semibold rounded-full">{post.category}</span>
          )}
          <span className="text-xs text-slate">
            {new Date(post.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </span>
          <span className="text-xs text-slate">by {post.author_name}</span>
        </div>

        <h1 className="font-display text-4xl font-bold mb-6 leading-tight">{post.title}</h1>

        {/* Article content — rendered as HTML (created by admins, not user input) */}
        <div
          className="prose prose-lg max-w-none
            [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-4
            [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-bold [&_h3]:mt-8 [&_h3]:mb-3
            [&_p]:text-slate [&_p]:leading-relaxed [&_p]:mb-4
            [&_a]:text-coral [&_a]:font-semibold
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4
            [&_li]:text-slate [&_li]:mb-1
            [&_blockquote]:border-l-4 [&_blockquote]:border-coral [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content) }}
        />

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="flex gap-2 mt-8 pt-6 border-t border-black/[0.06]">
            {post.tags.map((t: string) => (
              <span key={t} className="px-3 py-1 bg-paper rounded-full text-xs font-semibold text-slate">{t}</span>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-ink rounded-2xl p-8 text-center text-white">
          <h2 className="font-display text-2xl font-bold mb-2">Ready to send smarter emails?</h2>
          <p className="text-white/60 mb-6">Start free with MoreMail — AI-powered email marketing for modern teams.</p>
          <div className="flex justify-center">
            <LeadCaptureBox />
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-12">
            <h3 className="font-bold text-lg mb-4">Related articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map((r: any) => (
                <Link key={r.slug} to={`/blog/${r.slug}`}
                  className="bg-white rounded-xl border border-black/[0.06] p-4 hover:shadow-md transition-shadow">
                  <h4 className="font-semibold text-sm mb-1 line-clamp-2">{r.title}</h4>
                  <p className="text-xs text-slate line-clamp-2">{r.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </>
  );
}
